import { Link, useNavigate } from 'react-router-dom';
import { Search, Shield, TrendingUp, Award, Sparkles, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { motion } from 'framer-motion';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const scaleIn = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1
  }
};
export function Hero() {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  };
  return <section className="relative overflow-hidden bg-gradient-to-b from-accent to-background py-16 md:py-24">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute left-1/4 top-1/4 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute right-1/4 bottom-1/4 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="container-wide relative">
        <div className="mx-auto max-w-3xl text-center">
          {/* Badge */}
          <motion.div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5" initial="hidden" animate="visible" variants={fadeInUp} transition={{
          duration: 0.5,
          delay: 0
        }}>
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              Análisis ergonómicos verificados
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 className="mb-4 font-display text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl" initial="hidden" animate="visible" variants={fadeInUp} transition={{
          duration: 0.5,
          delay: 0.1
        }}>
            Tu silla está{' '}
            <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">destruyendo tu espalda!</span>
          </motion.h1>

          {/* Secondary Title */}
          <motion.h2 className="mb-4 text-xl font-semibold text-foreground sm:text-2xl md:text-3xl" initial="hidden" animate="visible" variants={fadeInUp} transition={{
          duration: 0.5,
          delay: 0.2
        }}>
            Encuentra la mejor silla de escritorio para tu salud
          </motion.h2>

          {/* Description */}
          <motion.p className="mb-8 text-lg text-muted-foreground md:text-xl" initial="hidden" animate="visible" variants={fadeInUp} transition={{
          duration: 0.5,
          delay: 0.3
        }}>
            Comparamos, analizamos y clasificamos las mejores sillas ergonómicas 
            disponibles en Amazon España. Mejora tu postura y productividad.
          </motion.p>

          {/* Quiz CTA */}
          <motion.div className="mb-8 p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20" initial="hidden" animate="visible" variants={scaleIn} transition={{
          duration: 0.4,
          delay: 0.4
        }}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-xs font-medium text-primary uppercase tracking-wide">
                    Personalizado · 3 min · Gratis
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Descubre qué silla es perfecta según tu altura, peso y necesidades
                </p>
              </div>
              <Link to="/quiz-sillas">
                <Button size="lg" className="whitespace-nowrap gap-2">
                  Hacer el Quiz
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Search */}
          <motion.form onSubmit={handleSearch} className="mx-auto mb-12 flex max-w-xl gap-2" initial="hidden" animate="visible" variants={fadeInUp} transition={{
          duration: 0.5,
          delay: 0.5
        }}>
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input type="search" placeholder="Buscar sillas ergonómicas, gaming..." className="h-12 pl-12 text-base" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <Button type="submit" size="lg" className="h-12 px-6">
              Buscar
            </Button>
          </motion.form>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8">
            {[{
            icon: Award,
            value: '300+',
            label: 'Sillas analizadas'
          }, {
            icon: TrendingUp,
            value: '50+',
            label: 'Guías de postura'
          }, {
            icon: Shield,
            value: '100%',
            label: 'Análisis imparciales'
          }].map((stat, index) => <motion.div key={stat.label} className="text-center" initial="hidden" animate="visible" variants={fadeInUp} transition={{
            duration: 0.5,
            delay: 0.6 + index * 0.1
          }}>
                <div className="flex items-center justify-center gap-1">
                  <stat.icon className="h-5 w-5 text-primary" />
                  <span className="text-2xl font-bold text-foreground md:text-3xl">{stat.value}</span>
                </div>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}